import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Upload, FileCode, Loader2, Shield, ShieldCheck, ShieldAlert, ShieldX,
  AlertTriangle, Info, ChevronDown, ChevronRight, Trash2, Download,
  Clock, Files, History, X, CheckCircle2, XCircle, ExternalLink,
  FolderUp, FileText, Scan
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  CRITICAL: { color: "bg-red-600 text-white", icon: ShieldX, textColor: "text-red-400" },
  HIGH: { color: "bg-orange-600 text-white", icon: ShieldAlert, textColor: "text-orange-400" },
  MEDIUM: { color: "bg-yellow-600 text-black", icon: AlertTriangle, textColor: "text-yellow-400" },
  LOW: { color: "bg-blue-600 text-white", icon: Info, textColor: "text-blue-400" },
  INFO: { color: "bg-gray-600 text-white", icon: Info, textColor: "text-gray-400" }
};

const SUPPORTED_EXTENSIONS = ['.tf', '.tfvars', '.yaml', '.yml', '.json', '.template', '.bicep'];

const FindingItem = ({ finding, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  const severity = finding.severity?.toUpperCase() || "MEDIUM";
  const config = severityConfig[severity] || severityConfig.MEDIUM;
  const SeverityIcon = config.icon;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-3 p-3 hover:bg-zinc-800/50 rounded-lg transition-colors cursor-pointer border border-zinc-800 mb-2">
          <Badge className={config.color}>
            <SeverityIcon className="w-3 h-3 mr-1" />
            {severity}
          </Badge>
          <div className="flex-1 text-left">
            <p className="text-sm text-zinc-200">{finding.title}</p>
            <span className="text-xs text-zinc-500">{finding.tool} • {finding.rule_id}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-500">
            <FileCode className="w-4 h-4" />
            <span className="text-xs font-mono">{finding.file_path}</span>
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 mb-4 p-4 bg-zinc-900 rounded-lg border border-zinc-800">
          <p className="text-sm text-zinc-300 mb-3">{finding.description}</p>
          {finding.remediation && (
            <div className="mb-2">
              <span className="text-xs text-zinc-500">Remediation: </span>
              <span className="text-sm text-emerald-400">{finding.remediation}</span>
            </div>
          )}
          {finding.reference_url && (
            <a href={finding.reference_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300">
              <ExternalLink className="w-3 h-3" /> Learn more
            </a>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const UploadScanTab = () => {
  const { token, user } = useAuth();
  const [files, setFiles] = useState([]);
  const [scanName, setScanName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [limits, setLimits] = useState(null);
  const [selectedHistoryScan, setSelectedHistoryScan] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fetchLimits = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/scan/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data);
    } catch (error) {
      console.error("Failed to fetch limits:", error);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/scan/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanHistory(response.data.scans || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLimits();
    fetchHistory();
  }, [fetchLimits, fetchHistory]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type: ${ext}`);
      return false;
    }
    if (limits && file.size > limits.limits.max_file_size_kb * 1024) {
      toast.error(`File ${file.name} exceeds max size (${limits.limits.max_file_size_kb}KB)`);
      return false;
    }
    return true;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = [...e.dataTransfer.files];
    const validFiles = [];
    
    for (const file of droppedFiles) {
      if (validateFile(file)) {
        const content = await readFileAsBase64(file);
        validFiles.push({
          file,
          filename: file.name,
          content,
          size: file.size
        });
      }
    }
    
    if (limits && files.length + validFiles.length > limits.limits.files_per_scan) {
      toast.error(`Maximum ${limits.limits.files_per_scan} files allowed per scan`);
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = [...e.target.files];
    const validFiles = [];
    
    for (const file of selectedFiles) {
      if (validateFile(file)) {
        const content = await readFileAsBase64(file);
        validFiles.push({
          file,
          filename: file.name,
          content,
          size: file.size
        });
      }
    }
    
    if (limits && files.length + validFiles.length > limits.limits.files_per_scan) {
      toast.error(`Maximum ${limits.limits.files_per_scan} files allowed per scan`);
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const runScan = async () => {
    if (files.length === 0) {
      toast.error("Please add files to scan");
      return;
    }

    if (limits && limits.usage.scans_remaining <= 0) {
      toast.error("Daily scan limit reached. Upgrade for more scans.");
      return;
    }

    setUploading(true);
    setScanResult(null);
    setSelectedHistoryScan(null);

    try {
      const response = await axios.post(
        `${API}/scan/upload`,
        {
          files: files.map(f => ({
            filename: f.filename,
            content: f.content,
            size: f.size
          })),
          scan_name: scanName || `Scan ${new Date().toLocaleString()}`
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000
        }
      );

      setScanResult(response.data);
      setFiles([]);
      setScanName("");
      fetchHistory();
      fetchLimits();

      const summary = response.data.summary;
      if (summary.critical > 0) {
        toast.error(`Scan complete: ${summary.critical} critical issues found!`);
      } else if (summary.high > 0) {
        toast.warning(`Scan complete: ${summary.high} high severity issues found.`);
      } else if (summary.total_findings > 0) {
        toast.info(`Scan complete: ${summary.total_findings} findings.`);
      } else {
        toast.success("Scan complete: No security issues found!");
      }
    } catch (error) {
      console.error("Scan failed:", error);
      toast.error(error.response?.data?.detail || "Scan failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const viewHistoryScan = async (scanId) => {
    try {
      const response = await axios.get(`${API}/scan/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedHistoryScan(response.data);
      setScanResult(null);
    } catch (error) {
      toast.error("Failed to load scan details");
    }
  };

  const deleteScan = async (scanId) => {
    try {
      await axios.delete(`${API}/scan/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scan deleted");
      fetchHistory();
      if (selectedHistoryScan?.id === scanId) {
        setSelectedHistoryScan(null);
      }
    } catch (error) {
      toast.error("Failed to delete scan");
    }
  };

  const downloadReport = async (scanId, format = 'json') => {
    try {
      const response = await axios.get(`${API}/scan/${scanId}/report?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scan_report_${scanId.slice(0, 8)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      fetchLimits();
      toast.success("Report downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download report");
    }
  };

  const displayResult = selectedHistoryScan || scanResult;

  return (
    <div className="space-y-6" data-testid="upload-scan-tab">
      {/* Limits & Usage Bar */}
      {limits && (
        <div className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-zinc-500">Tier: <span className="text-[#00f0ff] font-semibold capitalize">{limits.tier}</span></span>
            <span className="text-zinc-500">Scans Today: <span className="text-white">{limits.usage.scans_today}/{limits.limits.scans_per_day}</span></span>
            <span className="text-zinc-500">Files/Scan: <span className="text-white">{limits.limits.files_per_scan}</span></span>
          </div>
          {limits.tier === "architect" && (
            <span className="text-xs text-zinc-500">
              Report Downloads: {limits.usage.report_downloads_used}/{limits.limits.report_downloads}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Dropzone */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive 
                ? 'border-[#00f0ff] bg-[#00f0ff]/5' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              accept={SUPPORTED_EXTENSIONS.join(',')}
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="file-input"
            />
            <FolderUp className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-[#00f0ff]' : 'text-zinc-500'}`} />
            <h3 className="text-lg font-semibold text-white mb-2">Drop your IaC files here</h3>
            <p className="text-sm text-zinc-400 mb-3">or click to browse</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUPPORTED_EXTENSIONS.map(ext => (
                <span key={ext} className="px-2 py-1 bg-zinc-800 rounded text-xs font-mono text-zinc-400">{ext}</span>
              ))}
            </div>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <Files className="w-4 h-4 text-[#00f0ff]" />
                  Selected Files ({files.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-300 font-mono">{file.filename}</span>
                        <span className="text-xs text-zinc-500">({(file.size / 1024).toFixed(1)}KB)</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 flex items-center gap-3">
                  <Input
                    placeholder="Scan name (optional)"
                    value={scanName}
                    onChange={(e) => setScanName(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Button
                    onClick={runScan}
                    disabled={uploading || files.length === 0}
                    className="bg-gradient-to-r from-[#00f0ff] to-purple-600 hover:from-[#00f0ff]/90 hover:to-purple-600/90 text-white"
                    data-testid="run-scan-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Scan className="w-4 h-4 mr-2" />
                        Run Scan
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scan Results */}
          {displayResult && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#00f0ff]" />
                      {displayResult.scan_name || "Scan Results"}
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      {displayResult.file_count || displayResult.files_scanned?.length || 0} files scanned • 
                      {displayResult.scan_duration_seconds?.toFixed(2) || displayResult.scan_results?.scan_duration_seconds?.toFixed(2)}s
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadReport(displayResult.id, 'json')} className="text-zinc-400 border-zinc-700">
                      <Download className="w-4 h-4 mr-1" /> JSON
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadReport(displayResult.id, 'pdf')} className="text-zinc-400 border-zinc-700">
                      <Download className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[
                    { label: "Total", value: displayResult.summary?.total_findings || 0, color: "text-zinc-400" },
                    { label: "Critical", value: displayResult.summary?.critical || 0, color: "text-red-500" },
                    { label: "High", value: displayResult.summary?.high || 0, color: "text-orange-500" },
                    { label: "Medium", value: displayResult.summary?.medium || 0, color: "text-yellow-500" },
                    { label: "Low", value: displayResult.summary?.low || 0, color: "text-blue-500" },
                    { label: "Secrets", value: displayResult.summary?.secrets_detected || 0, color: "text-purple-500" }
                  ].map((item, i) => (
                    <div key={i} className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
                      <div className="text-xs text-zinc-500">{item.label}</div>
                    </div>
                  ))}
                </div>

                {/* Compliance Score */}
                <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
                  <div className={`text-4xl font-black ${
                    displayResult.compliance_score >= 80 ? 'text-emerald-400' :
                    displayResult.compliance_score >= 50 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {displayResult.compliance_score || 0}%
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Compliance Score</div>
                    <div className="text-xs text-zinc-500">Based on 31-tool security framework</div>
                  </div>
                </div>

                {/* Findings */}
                {(displayResult.scan_results?.findings || []).length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-1 pr-4">
                      {(displayResult.scan_results?.findings || []).map((finding, index) => (
                        <FindingItem key={index} finding={finding} index={index} />
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="p-8 text-center bg-emerald-900/20 rounded-lg border border-emerald-600/30">
                    <ShieldCheck className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                    <h3 className="text-lg font-bold text-emerald-400">All Clear!</h3>
                    <p className="text-sm text-zinc-400">No security issues found in your code.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* History Sidebar */}
        <div className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <History className="w-4 h-4 text-purple-400" />
                Scan History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                </div>
              ) : scanHistory.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 pr-2">
                    {scanHistory.map((scan) => (
                      <div
                        key={scan.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedHistoryScan?.id === scan.id
                            ? 'bg-purple-600/10 border-purple-500/50'
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                        }`}
                        onClick={() => viewHistoryScan(scan.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-white truncate max-w-[150px]">
                            {scan.scan_name}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); deleteScan(scan.id); }}
                            className="h-5 w-5 p-0 text-zinc-500 hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                          <Clock className="w-3 h-3" />
                          {new Date(scan.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            scan.summary?.critical > 0 ? 'border-red-500 text-red-400' :
                            scan.summary?.high > 0 ? 'border-orange-500 text-orange-400' :
                            scan.summary?.total_findings > 0 ? 'border-yellow-500 text-yellow-400' :
                            'border-emerald-500 text-emerald-400'
                          }`}>
                            {scan.summary?.total_findings || 0} findings
                          </Badge>
                          <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-400">
                            {scan.file_count} files
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No scan history yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UploadScanTab;
